import React, { useState, useEffect, useRef } from "react";

import {
  Banner,
  Modal,
  Stack,
  TextField,
  Thumbnail,
  TextStyle,
} from "@shopify/polaris";

import WeightDescriptorEditor, {
  descriptorsAreValid,
} from "./WeightDescriptorEditor";

/**
 * Per-item weight override editor modal for the Weight Options page.
 *
 * Lets the merchant configure a label and a list of weight+description tiers
 * for a specific product (identified by base PLU).
 *
 * The descriptor rows themselves live in `WeightDescriptorEditor` so the
 * sub-department and category override sections can render the same UI.
 * This modal continues to own product picking (header + label), the save
 * flow, and the error banner.
 *
 * Props
 * -----
 * @param {boolean}  open     - Whether the modal is visible.
 * @param {object}   override - Current override data to edit:
 *                              { plu, label, descriptors, productTitle, productImage }
 *                              `descriptors` is `[{ weight, description }]`.
 *                              `productTitle` / `productImage` are display-only;
 *                              both may be absent.
 * @param {function} onClose  - Called with no arguments to close without saving.
 * @param {function} onSave   - Called with `{ plu, label, descriptors }` on save.
 *                              May return a Promise; if it does, the Save button
 *                              enters a loading state until settlement and the
 *                              modal closes on success.  On rejection the modal
 *                              stays open so the user can retry.
 */
export default function PerItemOverrideModal({ open, override, onClose, onSave }) {
  const [label, setLabel] = useState("");
  const [descriptors, setDescriptors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const nextDescriptorIdRef = useRef(0);

  // Re-seed all internal state whenever the modal opens (or the override changes
  // while it is open — e.g. parent refreshes after a background save).
  useEffect(() => {
    if (open && override) {
      setLabel(
        override.label != null
          ? String(override.label)
          : override.productTitle
          ? String(override.productTitle)
          : ""
      );
      setDescriptors(
        Array.isArray(override.descriptors) && override.descriptors.length > 0
          ? override.descriptors.map((d) => ({
              id: nextDescriptorIdRef.current++,
              weight: d.weight != null ? String(d.weight) : "",
              description: d.description != null ? String(d.description) : "",
            }))
          : [
              {
                id: nextDescriptorIdRef.current++,
                weight: "",
                description: "",
              },
            ]
      );
      setSaving(false);
      setSaveError(null);
    }
  }, [open, override]);

  const canSave = descriptorsAreValid(descriptors);

  // ---------- Save ----------

  const handleSave = () => {
    if (!canSave) return;
    setSaveError(null);

    const cleanedDescriptors = descriptors.map((d) => ({
      weight: d.weight.trim(),
      description: d.description.trim(),
    }));

    const payload = {
      plu: override ? override.plu : "",
      label: label.trim(),
      descriptors: cleanedDescriptors,
    };

    const result = onSave(payload);

    if (result && typeof result.then === "function") {
      setSaving(true);
      result
        .then(() => {
          setSaving(false);
          // Parent should close the modal, but guard against it not doing so.
        })
        .catch((err) => {
          setSaving(false);
          setSaveError(
            (err && err.message) ||
              "An error occurred while saving. Please try again."
          );
        });
    }
    // Synchronous onSave — parent closes the modal.
  };

  const handleClose = () => {
    if (saving) return; // block close while mid-save
    onClose();
  };

  // ---------- Render helpers ----------

  const productImage =
    override && override.productImage ? override.productImage : null;
  const productTitle =
    override && override.productTitle ? override.productTitle : null;
  const plu = override ? override.plu : "";

  return (
    <Modal
      large
      open={open}
      title="Edit weight override"
      onClose={handleClose}
      primaryAction={{
        content: "Save",
        onAction: handleSave,
        loading: saving,
        disabled: !canSave,
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: handleClose,
        },
      ]}
    >
      {/* Product header */}
      <Modal.Section>
        <Stack alignment="center" spacing="tight">
          {productImage && (
            <Thumbnail
              source={productImage}
              alt={productTitle || plu}
              size="small"
            />
          )}
          <Stack vertical spacing="extraTight">
            {productTitle && (
              <TextStyle variation="strong">{productTitle}</TextStyle>
            )}
            <TextStyle variation="subdued">Base PLU: {plu}</TextStyle>
          </Stack>
        </Stack>
      </Modal.Section>

      {/* Label field */}
      <Modal.Section>
        <TextField
          label="Label"
          helpText="Optional display label for this override (e.g. 'Chicken breast weight options')."
          value={label}
          onChange={setLabel}
          autoComplete="off"
          placeholder={productTitle || plu}
        />
      </Modal.Section>

      {/* Descriptor rows — delegated to the shared editor */}
      <Modal.Section>
        <Stack vertical spacing="loose">
          <TextStyle variation="strong">Weight tiers</TextStyle>
          <WeightDescriptorEditor
            descriptors={descriptors}
            onChange={setDescriptors}
          />
        </Stack>
      </Modal.Section>

      {/* Save error banner (shown only when onSave Promise rejects) */}
      {saveError && (
        <Modal.Section>
          <Banner status="critical">{saveError}</Banner>
        </Modal.Section>
      )}
    </Modal>
  );
}
